import React, { useState } from 'react';
import { Eye, EyeOff, Lock, Unlock, Layers, ChevronRight, ChevronDown, Box, Edit2 } from 'lucide-react';
import type { BlockNode } from '../types';
import { BLOCK_MANIFESTS } from '../blocks/generated/manifest';

export interface LayerTreePanelProps {
  root: BlockNode;
  selectedId: string | null;
  onSelectNode: (id: string) => void;
  onToggleHide: (id: string, hidden: boolean) => void;
  onToggleLock: (id: string, locked: boolean) => void;
  onRenameNode?: (id: string, newName: string) => void;
  onReparentNode?: (sourceId: string, targetParentId: string, destIndex: number) => void;
}

export function LayerTreePanel({
  root,
  selectedId,
  onSelectNode,
  onToggleHide,
  onToggleLock,
  onRenameNode,
  onReparentNode,
}: LayerTreePanelProps): React.JSX.Element {
  return (
    <div className="flex flex-col h-full bg-card text-foreground select-none overflow-hidden">
      <div className="flex items-center justify-between p-3 border-b border-border bg-muted/20 shrink-0">
        <div className="flex items-center gap-2 font-bold text-xs text-foreground">
          <Layers className="w-3.5 h-3.5 text-[#3b97e3]" />
          <span>Layer Navigator</span>
        </div>
        <span className="text-[10px] text-muted-foreground font-mono">
          {root.children?.length || 0} top-level
        </span>
      </div>

      <div className="flex-1 overflow-y-auto p-2 no-scrollbar">
        <TreeNodeItem
          node={root}
          depth={0}
          selectedId={selectedId}
          onSelectNode={onSelectNode}
          onToggleHide={onToggleHide}
          onToggleLock={onToggleLock}
          onRenameNode={onRenameNode}
          onReparentNode={onReparentNode}
        />
      </div>
    </div>
  );
}

interface TreeNodeItemProps {
  node: BlockNode;
  depth: number;
  selectedId: string | null;
  onSelectNode: (id: string) => void;
  onToggleHide: (id: string, hidden: boolean) => void;
  onToggleLock: (id: string, locked: boolean) => void;
  onRenameNode?: (id: string, newName: string) => void;
  onReparentNode?: (sourceId: string, targetParentId: string, destIndex: number) => void;
}

const TreeNodeItem: React.FC<TreeNodeItemProps> = ({
  node,
  depth,
  selectedId,
  onSelectNode,
  onToggleHide,
  onToggleLock,
  onRenameNode,
  onReparentNode,
}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [isRenaming, setIsRenaming] = useState(false);
  const [renameVal, setRenameVal] = useState(node.customName || '');
  const [isDragOver, setIsDragOver] = useState(false);

  const hasChildren = Boolean(node.children && node.children.length > 0);
  const isSelected = selectedId === node.id;
  const manifest = BLOCK_MANIFESTS[node.type as keyof typeof BLOCK_MANIFESTS];
  const displayName = node.customName || manifest?.title || node.type;

  const handleDragStart = (e: React.DragEvent) => {
    e.stopPropagation();
    e.dataTransfer.setData('text/plain', node.id);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    const draggedId = e.dataTransfer.getData('text/plain');
    if (draggedId && draggedId !== node.id && onReparentNode) {
      onReparentNode(draggedId, node.id, 0);
    }
  };

  return (
    <div className="flex flex-col">
      <div
        draggable={!node.locked}
        onDragStart={handleDragStart}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`flex items-center justify-between group rounded px-2 py-1 cursor-pointer transition-colors text-xs ${
          isSelected
            ? 'bg-[#3b97e3]/15 text-[#3b97e3] font-bold border border-[#3b97e3]/30'
            : isDragOver
            ? 'bg-[#62c462]/20 border border-[#62c462]'
            : 'hover:bg-muted text-foreground'
        } ${node.hidden ? 'opacity-40' : ''}`}
        style={{ paddingLeft: `${depth * 12 + 6}px` }}
        onClick={() => onSelectNode(node.id)}
        onDoubleClick={(e) => {
          e.stopPropagation();
          setRenameVal(node.customName || displayName);
          setIsRenaming(true);
        }}
      >
        <div className="flex items-center gap-1.5 truncate">
          {hasChildren ? (
            <button
              type="button"
              className="w-4 h-4 flex items-center justify-center p-0 hover:bg-muted-foreground/10 rounded cursor-pointer"
              onClick={(e) => {
                e.stopPropagation();
                setCollapsed(!collapsed);
              }}
            >
              {collapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          ) : (
            <Box className="w-3.5 h-3.5 text-muted-foreground/60 shrink-0" />
          )}

          {isRenaming ? (
            <input
              type="text"
              autoFocus
              value={renameVal}
              onChange={(e) => setRenameVal(e.target.value)}
              onBlur={() => {
                setIsRenaming(false);
                if (onRenameNode) onRenameNode(node.id, renameVal.trim());
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  setIsRenaming(false);
                  if (onRenameNode) onRenameNode(node.id, renameVal.trim());
                } else if (e.key === 'Escape') {
                  setIsRenaming(false);
                }
              }}
              onClick={(e) => e.stopPropagation()}
              className="px-1 py-0 text-xs rounded border border-[#3b97e3] bg-background text-foreground focus:outline-none"
            />
          ) : (
            <span className="truncate">{displayName}</span>
          )}

          {node.htmlId && (
            <span className="text-[9px] font-mono text-muted-foreground/70">
              #{node.htmlId}
            </span>
          )}
        </div>

        {/* Action icons */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            type="button"
            className="w-5 h-5 flex items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground cursor-pointer"
            title="Rename Layer"
            onClick={(e) => {
              e.stopPropagation();
              setRenameVal(node.customName || displayName);
              setIsRenaming(true);
            }}
          >
            <Edit2 className="w-3 h-3" />
          </button>
          <button
            type="button"
            className="w-5 h-5 flex items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground cursor-pointer"
            title={node.hidden ? 'Show' : 'Hide'}
            onClick={(e) => {
              e.stopPropagation();
              onToggleHide(node.id, !node.hidden);
            }}
          >
            {node.hidden ? <EyeOff className="w-3 h-3 text-rose-500" /> : <Eye className="w-3 h-3" />}
          </button>
          <button
            type="button"
            className="w-5 h-5 flex items-center justify-center rounded hover:bg-muted text-muted-foreground hover:text-foreground cursor-pointer"
            title={node.locked ? 'Unlock' : 'Lock'}
            onClick={(e) => {
              e.stopPropagation();
              onToggleLock(node.id, !node.locked);
            }}
          >
            {node.locked ? <Lock className="w-3 h-3 text-amber-500" /> : <Unlock className="w-3 h-3" />}
          </button>
        </div>
      </div>

      {!collapsed && hasChildren && node.children && (
        <div className="flex flex-col">
          {node.children.map((child) => (
            <TreeNodeItem
              key={child.id}
              node={child}
              depth={depth + 1}
              selectedId={selectedId}
              onSelectNode={onSelectNode}
              onToggleHide={onToggleHide}
              onToggleLock={onToggleLock}
              onRenameNode={onRenameNode}
              onReparentNode={onReparentNode}
            />
          ))}
        </div>
      )}
    </div>
  );
};
